<template>
  <Dialog :open="!!tour" @update:open="onDialogUpdate">
    <DialogScrollContent size="lg" @interact-outside.prevent>
      <DialogHeader>
        <DialogTitle>
          {{ $t('Parks configuration for {tour}', {tour : tour?.name}) }}
        </DialogTitle>
      </DialogHeader>

      <div v-if="tour && (stages?.length > 0 || stores?.length > 0 )" class="mb-3">
        <div class="mb-3">
          <strong>
            {{ $t('Please select a context to configure the parks availability') }}
          </strong>
        </div>

        <Tabs default-value="tour" v-model="selectedContextId" class="tw:w-full">
          <TabsList class="tw:w-full">
            <template v-for="context in availableContexts">
              <TabsTrigger :value="context.id">
                {{ context.name }}
              </TabsTrigger>
            </template>
          </TabsList>
          <TabsContent value="tour">
            <div class="text-muted tw:mb-3">
              {{ $t('Selected parks will be available for the entire event') }}
            </div>
            <ParkableSection
              ref="tourSectionRef"
              :parkable="tour"
              :parkable-type="PARKABLE_TYPE_TOUR"
              :parks-available="parksAvailable"
              :filters="filters"
              :tour-owned-parks="tourOwnedParks"
              :tour-name="tour.name"
              :disallow-brand-parks="!!tour?.disallow_brand_parks"
              @loaded="(count) => updateParkCount('tour', tour.id, count)"
              @pending-changes="storePendingChanges"
            />
          </TabsContent>
          <TabsContent value="stage" class="tw:min-w-0">
            <div class="text-muted tw:mb-3">
              {{ $t('Selected parks will be visible only on specific stage') }}
            </div>
            <Accordion
              type="single"
              collapsible
              class="tw:space-y-1"
            >
              <AccordionItem v-for="stageItem in stages" :key="stageItem.id" :value="String(stageItem.id)" class="tw:border-0 tw:min-w-0">
                <AccordionTrigger class="tw:px-3 tw:py-2 tw:rounded-md tw:bg-zinc-100 tw:dark:bg-zinc-800 tw:hover:bg-zinc-200 tw:dark:hover:bg-zinc-700 tw:hover:no-underline">
                  <div class="tw:flex tw:items-center tw:justify-between tw:gap-2 tw:w-full">
                    <div class="tw:w-0 tw:flex-1">
                      <div class="tw:font-medium tw:truncate">{{ stageItem.name }}</div>
                      <div class="tw:text-sm tw:text-zinc-500 tw:truncate">{{ formatDateRange(stageItem.start_at, stageItem.end_at) }}</div>
                    </div>
                    <Badge class="tw:shrink-0 tw:whitespace-nowrap" :variant="badgeVariant('stages', stageItem.id)">
                      {{ $t('park selected', { count: displayCount('stages', stageItem.id) }, displayCount('stages', stageItem.id)) }}
                    </Badge>
                  </div>
                </AccordionTrigger>
                <AccordionContent>
                  <ParkableSection
                    :ref="(el) => setSectionRef('stages', stageItem.id, el)"
                    :parkable="stageItem"
                    :parkable-type="PARKABLE_TYPE_STAGE"
                    :parks-available="parksAvailable"
                    :filters="filters"
                    :tour-owned-parks="tourOwnedParks"
                    :tour-name="tour.name"
                    :disallow-brand-parks="!!tour?.disallow_brand_parks"
                    @loaded="(count) => updateParkCount('stages', stageItem.id, count)"
                    @selection-changed="(count) => updateLiveCount('stages', stageItem.id, count)"
                    @pending-changes="storePendingChanges"
                  />
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </TabsContent>
          <TabsContent value="store" class="tw:min-w-0">
            <div class="text-muted tw:mb-3">
              {{ $t('Selected parks will be visible only on specific store') }}
            </div>
            <Accordion
              type="single"
              collapsible
              class="tw:space-y-1 tw:min-w-0"
            >
              <AccordionItem v-for="storeItem in stores" :key="storeItem.id" :value="String(storeItem.id)" class="tw:border-0 tw:min-w-0">
                <AccordionTrigger class="tw:px-3 tw:py-2 tw:rounded-md tw:bg-zinc-100 tw:dark:bg-zinc-800 tw:hover:bg-zinc-200 tw:dark:hover:bg-zinc-700 tw:hover:no-underline">
                  <div class="tw:flex tw:items-center tw:justify-between tw:gap-2 tw:w-full">
                    <div class="tw:w-0 tw:flex-1">
                      <div class="tw:font-medium tw:truncate">{{ storeItem.name }}</div>
                      <div v-if="storeItem.location" class="tw:text-sm tw:text-zinc-500 tw:truncate">
                        {{ storeItem.location.address }} ({{ storeItem.location.country_name }})
                      </div>
                    </div>
                    <Badge class="tw:shrink-0 tw:whitespace-nowrap" :variant="badgeVariant('stores', storeItem.id)">
                      {{ $t('park selected', { count: displayCount('stores', storeItem.id) }, displayCount('stores', storeItem.id)) }}
                    </Badge>
                  </div>
                </AccordionTrigger>
                <AccordionContent>
                  <ParkableSection
                    :ref="(el) => setSectionRef('stores', storeItem.id, el)"
                    :parkable="storeItem"
                    :parkable-type="PARKABLE_TYPE_STORE"
                    :parks-available="parksAvailable"
                    :filters="filters"
                    :tour-owned-parks="tourOwnedParks"
                    :tour-name="tour.name"
                    :disallow-brand-parks="!!tour?.disallow_brand_parks"
                    @loaded="(count) => updateParkCount('stores', storeItem.id, count)"
                    @selection-changed="(count) => updateLiveCount('stores', storeItem.id, count)"
                    @pending-changes="storePendingChanges"
                  />
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </TabsContent>
        </Tabs>
      </div>

      <div v-else-if="tour">
        <ParkableSection
          ref="tourSectionRef"
          :parkable="tour"
          :parkable-type="PARKABLE_TYPE_TOUR"
          :parks-available="parksAvailable"
          :filters="filters"
          :tour-owned-parks="tourOwnedParks"
          :tour-name="tour.name"
          :disallow-brand-parks="!!tour?.disallow_brand_parks"
          @loaded="(count) => updateParkCount('tour', tour.id, count)"
          @pending-changes="storePendingChanges"
        />
      </div>

      <DialogFooter>
        <Button type="button" variant="secondary" :disabled="saving" @click="$emit('close')">
          {{ $t('Close') }}
        </Button>
        <Button type="button" :loading="saving" :disabled="!globalHasChanges" @click="saveAll">
          {{ $t('Save') }}
        </Button>
      </DialogFooter>
    </DialogScrollContent>
  </Dialog>
</template>

<script setup>
import { computed, ref, reactive, watch } from "vue";
import { useI18n } from "vue-i18n";
import { formatDateRange } from "@/utils/date";
import StageModel from "@/models/Stage";
import StoreModel from "@/models/Store";
import ParkModel from "@/models/Park";
import { Dialog, DialogScrollContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shadcn/components/ui/tabs';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/shadcn/components/ui/accordion';
import Badge from "@/components/ui/badge/Badge.vue";
import { Button } from "@/shadcn/components/ui/button";
import ParkableSection from "@/components/park/ParkableSection.vue";
import { toast } from "vue-sonner";

const PARKABLE_TYPE_TOUR = 'App\\Models\\Tour';
const PARKABLE_TYPE_STAGE = 'App\\Models\\Stage';
const PARKABLE_TYPE_STORE = 'App\\Models\\Store';

const emits = defineEmits([
  "change",
  "close"
]);

const { t } = useI18n();

const contexts = computed(() => [
  {
    id: 'tour',
    name: t('Entire event'),
    parkable_type: PARKABLE_TYPE_TOUR,
  },
  {
    id: 'stage',
    name: t('Specific stage'),
    parkable_type: PARKABLE_TYPE_STAGE,
  },
  {
    id: 'store',
    name: t('Specific store'),
    parkable_type: PARKABLE_TYPE_STORE,
  }
]);

const availableContexts = computed(() => {
  return contexts.value.filter(c => {
    if (c.id === 'stage') {
      return stages.value.length > 0;
    }
    if (c.id === 'store') {
      return stores.value.length > 0;
    }
    return true;
  });
});

const props = defineProps({
  tour: {
    type: Object,
  },
  parksAvailable: {
    type: Array,
  },
  parkableUrl: {
    type: String,
  },
  filters: {
    type: Object,
    default: () => {}
  },
});

const stages = ref([]);
const stores = ref([]);
const selectedContextId = ref(null);
const saving = ref(false);

const savedParkCounts = reactive({
  tour: {},
  stages: {},
  stores: {},
});

const liveParkCounts = reactive({
  tour: {},
  stages: {},
  stores: {},
});

const displayCount = (type, id) => {
  return liveParkCounts[type][id] ?? savedParkCounts[type][id] ?? 0;
};

const isCountDirty = (type, id) => {
  return liveParkCounts[type][id] !== undefined && liveParkCounts[type][id] !== savedParkCounts[type][id];
};

const badgeVariant = (type, id) => {
  if (isCountDirty(type, id)) {
    return 'yellow';
  }
  return displayCount(type, id) > 0 ? 'green' : 'zinc-light';
};

// Track section refs and pending changes from unmounted sections
const tourSectionRef = ref(null);
const sectionRefs = reactive({ stages: {}, stores: {} });
const pendingChanges = reactive({});

const setSectionRef = (type, id, el) => {
  if (el) {
    sectionRefs[type][id] = el;
    // Clear pending changes when a section mounts (live data takes precedence)
    const key = `${type}-${id}`;
    delete pendingChanges[key];
  } else {
    delete sectionRefs[type][id];
  }
};

const storePendingChanges = (payload) => {
  const typeMap = {
    [PARKABLE_TYPE_STAGE]: 'stages',
    [PARKABLE_TYPE_STORE]: 'stores',
    [PARKABLE_TYPE_TOUR]: 'tour',
  };
  const type = typeMap[payload.parkable_type] || 'tour';
  const key = `${type}-${payload.parkable_id}`;
  pendingChanges[key] = payload;
  // Keep live count updated from pending payload
  const selectedCount = payload.parks.filter(p => p.selected).length;
  liveParkCounts[type][payload.parkable_id] = selectedCount;
};

const globalHasChanges = computed(() => {
  // Check tour section
  if (tourSectionRef.value?.hasChanges) {
    return true;
  }
  // Check mounted stage/store sections
  for (const type of ['stages', 'stores']) {
    for (const id in sectionRefs[type]) {
      if (sectionRefs[type][id]?.hasChanges) {
        return true;
      }
    }
  }
  // Check pending changes from unmounted sections
  return Object.keys(pendingChanges).length > 0;
});

const tourOwnedParks = computed(() => {
  return props.tour?.parks?.filter(park => park.ownerable_type === 'App\\Models\\Tour') || [];
});

watch(
  () => props.tour,
  (value) => {
    selectedContextId.value = contexts.value[0].id;
    // Reset state
    Object.keys(pendingChanges).forEach(key => delete pendingChanges[key]);
    Object.keys(sectionRefs.stages).forEach(key => delete sectionRefs.stages[key]);
    Object.keys(sectionRefs.stores).forEach(key => delete sectionRefs.stores[key]);
    savedParkCounts.tour = {};
    savedParkCounts.stages = {};
    savedParkCounts.stores = {};
    liveParkCounts.tour = {};
    liveParkCounts.stages = {};
    liveParkCounts.stores = {};

    if (value) {
      setStages();
      setStores();
    }
  }
);

const onDialogUpdate = (isOpen) => {
  if (!isOpen) {
    emits('close');
  }
};

const setStages = async () => {
  if (!props.tour) {
    stages.value = [];
    return;
  }
  try {
    const stageModel = StageModel.where('tour_id', props.tour.id);
    const stageResource = await stageModel.limit(-1).get();
    stages.value = stageResource.data;
    await fetchParkCounts('stages', stages.value, 'stage_id');
  } catch (error) {
    console.error(error);
    toast.error(t('Failed to load stages'));
  }
};

const setStores = async () => {
  if (!props.tour) {
    stores.value = [];
    return;
  }
  try {
    const storeModel = StoreModel.where('tour_id', props.tour.id);
    const storeResource = await storeModel.limit(-1).get();
    stores.value = storeResource.data;
    await fetchParkCounts('stores', stores.value, 'store_id');
  } catch (error) {
    console.error(error);
    toast.error(t('Failed to load stores'));
  }
};

const fetchParkCounts = async (type, items, filterKey) => {
  const promises = items.map(async (item) => {
    let parkModel = ParkModel;
    const filters = { ...props.filters, [filterKey]: item.id };
    Object.entries(filters).forEach(([key, value]) => {
      parkModel = parkModel.where(key, value);
    });
    const parks = await parkModel.get();
    savedParkCounts[type][item.id] = parks.data.length;
  });
  await Promise.all(promises);
};

const updateParkCount = (type, id, count) => {
  savedParkCounts[type][id] = count;
  delete liveParkCounts[type][id];
};

const updateLiveCount = (type, id, count) => {
  liveParkCounts[type][id] = count;
};

const saveAll = async () => {
  saving.value = true;
  const parkables = [];

  // Collect from tour section
  if (tourSectionRef.value?.hasChanges) {
    parkables.push(tourSectionRef.value.getSelectionPayload());
  }

  // Collect from mounted stage/store sections
  for (const type of ['stages', 'stores']) {
    for (const id in sectionRefs[type]) {
      const section = sectionRefs[type][id];
      if (section?.hasChanges) {
        parkables.push(section.getSelectionPayload());
      }
    }
  }

  // Collect pending changes from unmounted sections
  for (const key in pendingChanges) {
    parkables.push(pendingChanges[key]);
  }

  if (parkables.length === 0) {
    saving.value = false;
    return;
  }

  try {
    const response = await axios.post(props.parkableUrl, {
      tour_id: props.tour.id,
      parkables,
    });

    // Update saved counts from response and clear live counts
    if (response.data.counts) {
      for (const [id, count] of Object.entries(response.data.counts)) {
        for (const parkable of parkables) {
          if (String(parkable.parkable_id) === String(id)) {
            const typeMap = {
              [PARKABLE_TYPE_TOUR]: 'tour',
              [PARKABLE_TYPE_STAGE]: 'stages',
              [PARKABLE_TYPE_STORE]: 'stores',
            };
            const type = typeMap[parkable.parkable_type];
            savedParkCounts[type][id] = count;
            delete liveParkCounts[type][id];
          }
        }
      }
    }

    // Clear pending changes
    Object.keys(pendingChanges).forEach(key => delete pendingChanges[key]);

    toast.success(t('Parks selection saved successfully'));
    emits("change");
  } catch (error) {
    console.error(error);
    toast.error(error?.response?.data?.message || error?.message || t('An error occurred while saving parks selection'));
  } finally {
    saving.value = false;
  }
};
</script>
